import * as THREE from 'three';
import { enableDepthFadeOnLineMaterial } from '../materials/DepthFadeUtils';
import { DensityCompositeMaterial } from '../materials/DensityCompositeMaterial';
import { Utils } from '../utils';
import type { RenderPipeline, RenderPipelineContext, RenderPipelineState } from './RenderPipeline';

/**
 * Texture type for the X-ray density accumulation target, or null when the
 * GPU cannot blend into a float target at all (then the legacy 8-bit path
 * is used). Full float is preferred: point counts stay exact up to 2^24.
 * Half float caps exact counts at 2048 per pixel, still 8x the 8-bit path.
 *
 * Note: EXT_color_buffer_float allows RENDERING to 32F targets, but
 * BLENDING into them additionally needs EXT_float_blend.
 */
export function densityAccumulationType(gl: WebGLRenderingContext | WebGL2RenderingContext): THREE.TextureDataType | null {
  const colorBufferFloat = !!gl.getExtension('EXT_color_buffer_float');
  if (colorBufferFloat && gl.getExtension('EXT_float_blend')) return THREE.FloatType;
  if (colorBufferFloat || gl.getExtension('EXT_color_buffer_half_float')) return THREE.HalfFloatType;
  return null;
}

/** Material with optional depth fade uniforms and setup flag */
interface DepthFadeMaterial extends THREE.Material {
  uniforms?: Record<string, { value: unknown }>;
  _depthFadeSetup?: boolean;
}

/** Measurement-like object with optional visual elements */
interface MeasureLike {
  depthFadeEnabled?: boolean;
  lineMaterial?: DepthFadeMaterial;
  backgroundLineMaterial?: DepthFadeMaterial;
  backgroundLineTouchMaterial?: DepthFadeMaterial;
  spheres?: Array<{
    mesh?: { material?: THREE.Material | THREE.Material[] };
    backgroundMesh?: { material?: THREE.Material | THREE.Material[] };
  }>;
  edgeLabels?: Array<{ material?: DepthFadeMaterial }>;
  coordinateLabels?: Array<{ material?: DepthFadeMaterial }>;
  areaLabel?: { material?: DepthFadeMaterial };
  volumeLabel?: { material?: DepthFadeMaterial };
  volumeLabels?: Array<{ material?: DepthFadeMaterial }>;
}

/** Per-view state for the Potree pipeline. */
export interface PotreePipelineState extends RenderPipelineState {
  readonly kind: 'potree';
  readonly dummyMesh: THREE.Mesh;
  readonly dummyScene: THREE.Scene;
  /**
   * Float accumulation target + composite for X-ray (density mode) views.
   * Allocated lazily on the first density render, since most views never
   * need them. `undefined` = not yet probed; `null` = unsupported GPU.
   */
  densityTarget?: THREE.WebGLRenderTarget | null;
  densityComposite?: DensityCompositeMaterial;
}

export class PotreeRenderer implements RenderPipeline<PotreePipelineState> {
  readonly kind = 'potree' as const;
  measurementDepthFadeEnabled: boolean;

  constructor() {
    // Global toggle for shader-based depth fade on measurements (default: enabled)
    this.measurementDepthFadeEnabled = true;
  }

  createState(): PotreePipelineState {
    const dummyScene = new THREE.Scene();
    const geometry = new THREE.SphereGeometry(0.001, 2, 2);
    const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial());
    mesh.position.set(36453, 35163, 764712);
    dummyScene.add(mesh);
    return { kind: 'potree', dummyMesh: mesh, dummyScene };
  }

  disposeState(state: PotreePipelineState): void {
    state.densityTarget?.dispose();
    state.densityComposite?.dispose();
    state.dummyMesh.geometry.dispose();
    if (Array.isArray(state.dummyMesh.material)) {
      state.dummyMesh.material.forEach((m) => m.dispose());
    } else {
      state.dummyMesh.material.dispose();
    }
  }

  clear(ctx: RenderPipelineContext, _state: PotreePipelineState): void {
    // Disable clear color to allow 2D maps to show through
    ctx.threeRenderer.setClearColor(0x000000, 0);
    ctx.threeRenderer.clear();
  }

  /**
   * Lazily allocate the float accumulation target for density mode. Returns
   * null when the GPU cannot blend into a float target.
   */
  private densityTarget(ctx: RenderPipelineContext, state: PotreePipelineState): THREE.WebGLRenderTarget | null {
    if (state.densityTarget === undefined) {
      const type = densityAccumulationType(ctx.threeRenderer.getContext());
      if (type === null) {
        console.warn('[PotreeRenderer] Float render targets unsupported — X-ray density falls back to 8-bit accumulation');
        state.densityTarget = null;
      } else {
        state.densityTarget = new THREE.WebGLRenderTarget(ctx.width, ctx.height, {
          minFilter: THREE.NearestFilter,
          magFilter: THREE.NearestFilter,
          format: THREE.RGBAFormat,
          type,
          // Density mode never depth-tests (every point must contribute).
          depthBuffer: false,
          stencilBuffer: false,
        });
      }
    }
    return state.densityTarget;
  }

  /**
   * High-precision X-ray pass: accumulate a per-pixel point count in a float
   * target, then composite it to the current framebuffer via a tone-map.
   * Returns false when unsupported, so the caller draws the legacy way.
   */
  private renderDensityAccumulated(ctx: RenderPipelineContext, state: PotreePipelineState): boolean {
    const { viewer, threeRenderer, pRenderer, camera, width, height, clipSpheres } = ctx;
    const target = this.densityTarget(ctx, state);
    if (!target) return false;

    if (target.width !== width || target.height !== height) target.setSize(width, height);

    const previousTarget = threeRenderer.getRenderTarget();
    threeRenderer.setRenderTarget(target);
    threeRenderer.setClearColor(0x000000, 0);
    threeRenderer.clear(true, false, false);

    // Each point adds 1 to every channel (the shader emits white in density
    // mode); the composite reads the count from R.
    const gl = threeRenderer.getContext();
    pRenderer.render(viewer.scene.scenePointCloud, camera, target, {
      clipSpheres,
      orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
      blendFunc: [gl.ONE, gl.ONE],
      depthTest: false,
      depthWrite: false,
    });

    threeRenderer.setRenderTarget(previousTarget);

    if (!state.densityComposite) state.densityComposite = new DensityCompositeMaterial();
    state.densityComposite.setDensityTexture(target.texture);
    state.densityComposite.setAlpha(viewer.xrayDensityAlpha);
    Utils.screenPass.render(threeRenderer, state.densityComposite);
    return true;
  }

  render(ctx: RenderPipelineContext, state: PotreePipelineState): void {
    const { viewer, threeRenderer, pRenderer, camera, clipSpheres, overlays } = ctx;

    viewer.dispatchEvent({ type: 'render.pass.begin', viewer });

    // Force EDL off for the plain Potree path. (Material override stack handles
    // restoration if a per-view useEDL override was active.)
    for (const pointcloud of viewer.scene.pointclouds) {
      pointcloud.material.useEDL = false;
    }

    // X-ray views put every visible cloud in density mode; accumulate those
    // in a float target so the per-point alpha is not quantized to 1/255.
    const visiblePointClouds = viewer.scene.pointclouds.filter((pc) => pc.visible);
    const allDensity = visiblePointClouds.length > 0 && visiblePointClouds.every((pc) => pc.material.densityMode);
    const accumulated = allDensity && this.renderDensityAccumulated(ctx, state);

    if (!accumulated) {
      pRenderer.render(viewer.scene.scenePointCloud, camera, null, {
        clipSpheres,
        orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
      });
    }

    // Snapshot the framebuffer for the wall-skin lines' invert shader NOW —
    // straight after the point cloud, before ANY overlay scene renders — so
    // the capture can never contain the lines themselves (a capture taken
    // later in the pass risks feeding the lines their own previous output,
    // which oscillates into noise).
    if (overlays.floorplan && viewer.scene.wallSketches.length > 0) {
      viewer.backdropCapture.capture(threeRenderer);
    }

    // render scene
    threeRenderer.render(viewer.scene.scene, camera);

    if (overlays.volumes) threeRenderer.render(viewer.volumeScene, camera);

    // Always update measurement materials to sync depth test setting
    this.updateMeasurementMaterials(ctx);

    if (!this.measurementDepthFadeEnabled) {
      // clear depth so measurements render on top
      threeRenderer.clearDepth();
    }

    if (overlays.measurements) threeRenderer.render(viewer.measurementScene, camera);
    if (overlays.planes) threeRenderer.render(viewer.planeScene, camera);

    if (overlays.controls && viewer.controls.sceneControls) threeRenderer.render(viewer.controls.sceneControls, camera);
    if (overlays.clippingVolumeMesh) threeRenderer.render(viewer.clippingTool.sceneVolume, camera);
    if (overlays.floorplan) threeRenderer.render(viewer.floorplanTool.scene, camera);
    if (overlays.toolOverlay) {
      for (const toolScene of viewer.toolOverlayScenes) threeRenderer.render(toolScene, camera);
    }

    // Top-most UI overlay: clear depth first so its contents always draw over
    // every other scene (point cloud, floorplan/skeleton meshes, etc.).
    if (viewer.uiScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.uiScene, camera);
    }

    // Transform gizmos render LAST — the manipulation widget must never be
    // buried under glyphs/overlays (its handles sit ON selected objects).
    if (overlays.gizmo && viewer.gizmoScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.gizmoScene, camera);
    }

    viewer.dispatchEvent({ type: 'render.pass.end', viewer });
  }

  /**
   * Update measurement materials to enable/disable depth testing
   * Note: PotreeRenderer doesn't have EDL depth texture, so uses simple depth test
   */
  updateMeasurementMaterials(ctx: RenderPipelineContext) {
    const { viewer } = ctx;
    const measurements = viewer.scene.measurements || [];

    for (const measure of measurements) {
      this.updateMeasureMaterials(measure);
    }

    // Update room materials
    const rooms = viewer.scene.rooms || [];
    for (const room of rooms) {
      this.updateMeasureMaterials(room);
    }

    // Update skeleton materials
    const wallSketches = viewer.scene.wallSketches || [];
    for (const skeleton of wallSketches) {
      this.updateMeasureMaterials(skeleton);
    }

    // Update outline materials (outer ring + each hole's line/background pair).
    // Outlines hold N hole materials beyond the standard pair, so apply the
    // same depth-fade configuration to each in addition to the outer pair.
    const outlines = viewer.scene.outlines || [];
    for (const outline of outlines) {
      this.updateMeasureMaterials(outline);
      const enabled = this.measurementDepthFadeEnabled && (outline.depthFadeEnabled ?? true);
      for (const mat of outline.holeLineMaterials) {
        const shaderMat = mat as DepthFadeMaterial;
        if (!shaderMat._depthFadeSetup) {
          enableDepthFadeOnLineMaterial(mat);
        }
        if (shaderMat.uniforms && shaderMat.uniforms.uDepthFadeEnabled) {
          shaderMat.uniforms.uDepthFadeEnabled.value = false;
        }
        mat.depthTest = enabled;
        mat.polygonOffset = enabled;
        mat.polygonOffsetFactor = -4;
        mat.polygonOffsetUnits = -4;
      }
    }

    // Update plane materials (depth test + polygon offset, same as measures)
    const planes = viewer.scene.planes || [];
    for (const plane of planes) {
      const enabled = this.measurementDepthFadeEnabled && (plane.depthFadeEnabled ?? true);
      const materials = Array.isArray(plane.circle.material) ? plane.circle.material : [plane.circle.material];
      for (const mat of materials) {
        // Disable shader-based depth fade (PotreeRenderer has no depth texture)
        const shaderMat = mat as DepthFadeMaterial;
        if (shaderMat.uniforms?.uDepthFadeEnabled) {
          shaderMat.uniforms.uDepthFadeEnabled.value = false;
        }
        mat.depthTest = enabled;
        mat.polygonOffset = enabled;
        mat.polygonOffsetFactor = -4;
        mat.polygonOffsetUnits = -4;
      }
    }
  }

  /**
   * Update a single measure's materials with depth test setting
   * PotreeRenderer doesn't have depth texture, so we disable shader-based depth fade
   * and use hardware depth test instead.
   */
  updateMeasureMaterials(measure: MeasureLike) {
    const enabled = this.measurementDepthFadeEnabled && (measure.depthFadeEnabled ?? true);

    // Helper to configure LineMaterial for depth-aware rendering
    const configureLineMaterial = (material: DepthFadeMaterial | undefined) => {
      if (!material) return;

      // Ensure depth fade shader is set up (needed for uniform access)
      if (!material._depthFadeSetup) {
        enableDepthFadeOnLineMaterial(material as unknown as import('three/examples/jsm/lines/LineMaterial.js').LineMaterial);
      }

      // Disable shader-based depth fade - PotreeRenderer doesn't have depth texture
      if (material.uniforms && material.uniforms.uDepthFadeEnabled) {
        material.uniforms.uDepthFadeEnabled.value = false;
      }

      // Use hardware depth test instead
      material.depthTest = enabled;
      // Use polygon offset to push measurements slightly forward in depth
      material.polygonOffset = enabled;
      material.polygonOffsetFactor = -4;
      material.polygonOffsetUnits = -4;
    };

    // Helper to configure shader materials (spheres, labels)
    const configureShaderMaterial = (material: THREE.Material | THREE.Material[] | undefined) => {
      if (!material) return;
      if (Array.isArray(material)) {
        material.forEach((m) => configureShaderMaterial(m));
        return;
      }

      // Disable shader-based depth fade
      const mat = material as DepthFadeMaterial;
      if (mat.uniforms && mat.uniforms.uDepthFadeEnabled) {
        mat.uniforms.uDepthFadeEnabled.value = false;
      }

      // Use hardware depth test instead
      material.depthTest = enabled;
      material.polygonOffset = enabled;
      material.polygonOffsetFactor = -4;
      material.polygonOffsetUnits = -4;
    };

    // Update line materials
    configureLineMaterial(measure.lineMaterial);
    configureLineMaterial(measure.backgroundLineMaterial);
    configureLineMaterial(measure.backgroundLineTouchMaterial);

    // Update sphere materials
    if (measure.spheres) {
      for (const sphere of measure.spheres) {
        configureShaderMaterial(sphere.mesh?.material);
        configureShaderMaterial(sphere.backgroundMesh?.material);
      }
    }

    // Update label materials
    if (measure.edgeLabels) {
      for (const label of measure.edgeLabels) {
        configureShaderMaterial(label.material);
      }
    }
    if (measure.coordinateLabels) {
      for (const label of measure.coordinateLabels) {
        configureShaderMaterial(label.material);
      }
    }
    if (measure.areaLabel) {
      configureShaderMaterial(measure.areaLabel.material);
    }
    // Handle volumeLabel (rooms) and volumeLabels array (measures)
    if (measure.volumeLabel) {
      configureShaderMaterial(measure.volumeLabel.material);
    }
    if (measure.volumeLabels) {
      for (const label of measure.volumeLabels) {
        configureShaderMaterial(label.material);
      }
    }
  }

  /**
   * Enable or disable measurement depth testing.
   * When enabled, measurements behind the point cloud will be hidden.
   */
  setMeasurementDepthFadeEnabled(enabled: boolean) {
    this.measurementDepthFadeEnabled = enabled;
  }
}
